import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import roomsService from '../services/roomsService'
import { useTranslation } from '../hooks/use-i18n'
import { Room, LeagueEnum } from '../types/room'

interface EditRoomModalProps {
  room: Room
  onClose: () => void
  onSuccess: () => void
}

interface EditRoomForm {
  name: string
  oneZero: string
  twoZero: string
  twoOne: string
  zeroZero: string
  oneOne: string
  twoTwo: string
  threeZero: string
  threeOne: string
  threeTwo: string
  threeThree: string
  fourZero: string
  fourOne: string
  fourTwo: string
  fourThree: string
  fourFour: string
  fiveAndMore: string
  fiveFiveMore: string
  correctResultPoints: string
  correctDrawPoints: string
  championAtStart: string
  championSecondChance: string
  topScorerPoints: string
  enableFutureChampionStart: boolean
  enableFutureChampionSecondChance: boolean
  enableTopScorer: boolean
  agentBanterEnabled: boolean
  winnerAdditionalTime: string
  winnerPenalties: string
}

type FootballScoreFieldKey = Extract<
  keyof EditRoomForm,
  | 'oneZero'
  | 'twoZero'
  | 'twoOne'
  | 'zeroZero'
  | 'oneOne'
  | 'twoTwo'
  | 'threeZero'
  | 'threeOne'
  | 'threeTwo'
  | 'threeThree'
  | 'fourZero'
  | 'fourOne'
  | 'fourTwo'
  | 'fourThree'
  | 'fourFour'
  | 'fiveAndMore'
  | 'fiveFiveMore'
>

// Same per-score keys and defaults as CreateRoomModal / mobileBrc / nest-brc
const FOOTBALL_SCORE_FIELDS: { key: FootballScoreFieldKey; labelKey: string; default: string }[] = [
  { key: 'oneZero', labelKey: 'createRoom.score1_0', default: '1' },
  { key: 'twoZero', labelKey: 'createRoom.score2_0', default: '1' },
  { key: 'twoOne', labelKey: 'createRoom.score2_1', default: '1' },
  { key: 'zeroZero', labelKey: 'createRoom.score0_0', default: '2' },
  { key: 'oneOne', labelKey: 'createRoom.score1_1', default: '2' },
  { key: 'twoTwo', labelKey: 'createRoom.score2_2', default: '2' },
  { key: 'threeZero', labelKey: 'createRoom.score3_0', default: '2' },
  { key: 'threeOne', labelKey: 'createRoom.score3_1', default: '2' },
  { key: 'threeTwo', labelKey: 'createRoom.score3_2', default: '2' },
  { key: 'threeThree', labelKey: 'createRoom.score3_3', default: '3' },
  { key: 'fourZero', labelKey: 'createRoom.score4_0', default: '3' },
  { key: 'fourOne', labelKey: 'createRoom.score4_1', default: '3' },
  { key: 'fourTwo', labelKey: 'createRoom.score4_2', default: '3' },
  { key: 'fourThree', labelKey: 'createRoom.score4_3', default: '3' },
  { key: 'fourFour', labelKey: 'createRoom.score4_4', default: '4' },
  { key: 'fiveAndMore', labelKey: 'createRoom.score5plus', default: '5' },
  { key: 'fiveFiveMore', labelKey: 'createRoom.score5_5plus', default: '5' },
]

const getLeagueKey = (league: LeagueEnum) =>
  'createRoom.league' + league.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('')

function getDefaultFromSetting(
  setting: Record<string, number | string | boolean> | undefined,
  key: string,
  fallback: string
): string {
  const v = setting?.[key]
  return v != null ? String(v) : fallback
}

function getBoolFromSetting(
  setting: Record<string, number | string | boolean> | undefined,
  key: string,
  fallback: boolean
): boolean {
  const v = setting?.[key]
  if (v === true || v === 'true') return true
  if (v === false || v === 'false') return false
  return fallback
}

function buildFootballScoreDefaults(
  setting: Record<string, number | string | boolean> | undefined,
): Pick<EditRoomForm, FootballScoreFieldKey> {
  const result = {} as Pick<EditRoomForm, FootballScoreFieldKey>
  for (const { key, default: d } of FOOTBALL_SCORE_FIELDS) {
    result[key] = getDefaultFromSetting(setting, key, d)
  }
  return result
}

const EditRoomModal: React.FC<EditRoomModalProps> = ({ room, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLeagues, setSelectedLeagues] = useState<LeagueEnum[]>(room.leagues || [])

  const setting = room.setting as unknown as Record<string, number | string | boolean> | undefined

  const defaultValues: EditRoomForm = {
    name: room.name,
    ...buildFootballScoreDefaults(setting),
    correctResultPoints: getDefaultFromSetting(setting, 'correctResultPoints', '1'),
    correctDrawPoints: getDefaultFromSetting(setting, 'correctDrawPoints', '1'),
    championAtStart: getDefaultFromSetting(setting, 'championAtStart', '20'),
    championSecondChance: getDefaultFromSetting(setting, 'championSecondChance', '10'),
    topScorerPoints: getDefaultFromSetting(setting, 'topScorerPoints', '20'),
    enableFutureChampionStart: getBoolFromSetting(setting, 'enableFutureChampionStart', true),
    enableFutureChampionSecondChance: getBoolFromSetting(setting, 'enableFutureChampionSecondChance', true),
    enableTopScorer: getBoolFromSetting(setting, 'enableTopScorer', false),
    agentBanterEnabled: Boolean(room.agentBanterEnabled),
    winnerAdditionalTime: getDefaultFromSetting(setting, 'winnerAdditionalTime', '2'),
    winnerPenalties: getDefaultFromSetting(setting, 'winnerPenalties', '3'),
  }

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<EditRoomForm>({
    defaultValues,
  })

  useEffect(() => {
    setValue('name', room.name)
    FOOTBALL_SCORE_FIELDS.forEach(({ key, default: d }) => {
      setValue(key, getDefaultFromSetting(setting, key, d))
    })
    setValue('correctResultPoints', getDefaultFromSetting(setting, 'correctResultPoints', '1'))
    setValue('correctDrawPoints', getDefaultFromSetting(setting, 'correctDrawPoints', '1'))
    setValue('championAtStart', getDefaultFromSetting(setting, 'championAtStart', '20'))
    setValue('championSecondChance', getDefaultFromSetting(setting, 'championSecondChance', '10'))
    setValue('topScorerPoints', getDefaultFromSetting(setting, 'topScorerPoints', '20'))
    setValue('enableFutureChampionStart', getBoolFromSetting(setting, 'enableFutureChampionStart', true))
    setValue('enableFutureChampionSecondChance', getBoolFromSetting(setting, 'enableFutureChampionSecondChance', true))
    setValue('enableTopScorer', getBoolFromSetting(setting, 'enableTopScorer', false))
    setValue('agentBanterEnabled', Boolean(room.agentBanterEnabled))
    setValue('winnerAdditionalTime', getDefaultFromSetting(setting, 'winnerAdditionalTime', '2'))
    setValue('winnerPenalties', getDefaultFromSetting(setting, 'winnerPenalties', '3'))
    setSelectedLeagues(room.leagues || [])
  }, [room, setting, setValue])

  const handleLeagueChange = (league: LeagueEnum, checked: boolean) => {
    setSelectedLeagues(prev =>
      checked ? [...prev, league] : prev.filter(l => l !== league)
    )
  }

  const onSubmit = async (data: EditRoomForm) => {
    try {
      setIsLoading(true)
      await roomsService.updateRoom(room._id, {
        name: data.name.trim(),
        leagues: selectedLeagues.length > 0 ? selectedLeagues : room.leagues,
        setting: {
          oneZero: data.oneZero,
          twoZero: data.twoZero,
          twoOne: data.twoOne,
          zeroZero: data.zeroZero,
          oneOne: data.oneOne,
          twoTwo: data.twoTwo,
          threeZero: data.threeZero,
          threeOne: data.threeOne,
          threeTwo: data.threeTwo,
          threeThree: data.threeThree,
          fourZero: data.fourZero,
          fourOne: data.fourOne,
          fourTwo: data.fourTwo,
          fourThree: data.fourThree,
          fourFour: data.fourFour,
          fiveAndMore: data.fiveAndMore,
          fiveFiveMore: data.fiveFiveMore,
          correctResultPoints: data.correctResultPoints,
          correctDrawPoints: data.correctDrawPoints,
          championAtStart: data.championAtStart,
          championSecondChance: data.championSecondChance,
          topScorerPoints: data.topScorerPoints,
          enableFutureChampionStart: data.enableFutureChampionStart,
          enableFutureChampionSecondChance: data.enableFutureChampionSecondChance,
          enableTopScorer: data.enableTopScorer,
          winnerAdditionalTime: data.winnerAdditionalTime,
          winnerPenalties: data.winnerPenalties,
        },
        agentBanterEnabled: data.agentBanterEnabled,
      })
      toast.success(t('common.success'))
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl mx-4 glass-strong rounded-2xl p-5 sm:p-6 my-8 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-display-2 font-bold text-foreground flex items-center gap-2">
            🏆 {t('room.editRoomTitle')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all" aria-label={t('common.close')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 overflow-y-auto">
          <div>
            <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('createRoom.roomName')}
            </label>
            <input
              {...register('name', { required: t('createRoom.roomNameRequired') })}
              type="text"
              className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder={t('createRoom.placeholderRoomName')}
            />
            {errors.name && <p className="mt-2 text-small text-destructive">{errors.name.message}</p>}
          </div>

          <div>
            <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('createRoom.selectLeagues')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(LeagueEnum).map((league) => (
                <label key={league} className="flex items-center p-3 rounded-xl bg-muted/10 border border-muted/20 hover:border-muted/40 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedLeagues.includes(league)}
                    onChange={(e) => handleLeagueChange(league, e.target.checked)}
                    className="mr-3 w-4 h-4 text-primary bg-muted/10 border-muted/20 rounded focus:ring-primary/20 focus:ring-1"
                  />
                  <span className="text-nav text-foreground font-medium">
                    {t(getLeagueKey(league))}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-xl p-4 border border-muted/20">
            <h3 className="text-display-2 font-semibold text-foreground mb-1">{t('createRoom.pointsDistribution')}</h3>
            <p className="text-small text-muted-foreground mb-4">{t('createRoom.pointsDistributionDesc')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.correctResultPoints')}</label>
                <input {...register('correctResultPoints', { required: true, min: 0 })} type="number" min="0" placeholder="1" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.correctDrawPoints')}</label>
                <input {...register('correctDrawPoints', { required: true, min: 0 })} type="number" min="0" placeholder="1" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {FOOTBALL_SCORE_FIELDS.map(({ key, labelKey, default: d }) => (
                <div key={key}>
                  <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                    {t(labelKey)}
                  </label>
                  <input
                    {...register(key, { required: true, min: 0 })}
                    type="number"
                    min="0"
                    placeholder={d}
                    className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-muted/20 space-y-3">
              <h4 className="text-small font-semibold text-muted-foreground uppercase tracking-wider">{t('createRoom.tournamentExtras')}</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...register('enableFutureChampionStart')} className="w-4 h-4" />
                <span className="text-nav text-foreground">{t('createRoom.enableFutureChampionStart')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...register('enableFutureChampionSecondChance')} className="w-4 h-4" />
                <span className="text-nav text-foreground">{t('createRoom.enableFutureChampionSecondChance')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...register('enableTopScorer')} className="w-4 h-4" />
                <span className="text-nav text-foreground">{t('createRoom.enableTopScorer')}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer col-span-full">
                <input type="checkbox" {...register('agentBanterEnabled')} className="w-4 h-4 mt-1" />
                <span className="text-nav text-foreground">
                  {t('agent.banterEnabled')}
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">{t('agent.banterEnabledHint')}</span>
                </span>
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-muted/20">
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.championAtStart')}</label>
                <input {...register('championAtStart', { required: true, min: 0 })} type="number" min="0" placeholder="20" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.championSecondChance')}</label>
                <input {...register('championSecondChance', { required: true, min: 0 })} type="number" min="0" placeholder="10" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.topScorerPoints')}</label>
                <input {...register('topScorerPoints', { required: true, min: 0 })} type="number" min="0" placeholder="20" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.winnerExtraTimePoints')}</label>
                <input {...register('winnerAdditionalTime', { required: true, min: 0 })} type="number" min="0" placeholder="2" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="text-small font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">{t('createRoom.winnerPenaltiesPoints')}</label>
                <input {...register('winnerPenalties', { required: true, min: 0 })} type="number" min="0" placeholder="3" className="w-full px-3 py-2 rounded-xl bg-muted/10 border border-muted/20 text-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 sm:py-3 rounded-xl bg-muted/20 border border-muted/20 text-foreground font-semibold text-small sm:text-nav hover:bg-muted/30 transition-all"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 sm:py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '...' : t('room.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditRoomModal
